import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { UserTable } from './UserTable';
import { EmptyState } from './EmptyState';

interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  created_at: string;
  is_verified: boolean;
  auth_user_id: string | null;
  created_from: 'self_registered' | 'invited';
}

interface UserListContainerProps {
  searchTerm: string;
  onEditUser: (user: Profile) => void;
  onDeleteUser: (user: Profile) => void;
  onImpersonateUser: (user: Profile) => void;
  impersonatingUserId: string | null;
}

export const UserListContainer = React.memo(({
  searchTerm,
  onEditUser,
  onDeleteUser,
  onImpersonateUser,
  impersonatingUserId
}: UserListContainerProps) => {
  const { session, refreshSession } = useAuth();
  
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['members', searchTerm, session?.access_token],
    queryFn: async () => {
      // Ensure we have a valid session before proceeding
      if (!session) {
        throw new Error('No active session - please refresh the page');
      }
      
      // Validate session is not expired
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = session.expires_at ? (session.expires_at - now) : 0;
      
      // Refresh session if it's about to expire or already expired
      if (timeUntilExpiry < 60) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          throw new Error('Session refresh failed - please sign in again');
        }
      }
      
      let query = supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      return data as Profile[];
    },
    enabled: !!session, // Only run query when we have a session
    retry: (failureCount, error) => {
      return failureCount < 2;
    },
    retryDelay: 2000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  if (isLoading && !users) {
    return <div>Loading members...</div>;
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 border border-red-200 rounded-lg">
        <h3 className="font-semibold">Error loading members</h3>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  if (users && users.length > 0) {
    return (
      <UserTable
        users={users}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}
        onImpersonateUser={onImpersonateUser}
        impersonatingUserId={impersonatingUserId}
      />
    );
  }

  return <EmptyState searchTerm={searchTerm} />;
});

UserListContainer.displayName = 'UserListContainer';